'use client';

import { useEffect } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useLocale } from 'next-intl';
import type { SupportedLocale } from '@/src/i18n/routing';

import type { AreaResponse } from '@/src/lib/api';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import {
  type ActiveListingState,
  type MapBorderState,
  type MapState,
  mapStateKeys,
  type PendingListingNavigationState,
} from '@/src/lib/hooks/map/mapStateKeys';
import { areaKeys, currentAreaKeys, listingDetailKeys } from '@/src/lib/hooks/query';
import { resolveInitialShowBorder } from '@/src/lib/map/borderPreference';

type Props = {
  maloneLotIdEncoded: string;
  pdpType: 'unit' | 'building';
  unit?: string | null;
  listing: PropertyDetailListing;
  initialMapPosition: MapState | null;
  areaData: AreaResponse | null;
  initialShowBorder: boolean;
  /** True when the URL carried a `border` value, which outranks the stored preference. */
  hasExplicitBorderParam?: boolean;
};

/**
 * Seeds the React Query cache with everything needed for a direct-load HDP
 * visit so the client shell paints without a duplicate listing fetch.
 *
 * Every seed is guarded against overwrite: on SRP ↔ HDP navigation the cache
 * is already populated by MapStateHydrator / LiveListingsGrid and we must not
 * clobber the user's pan/zoom/filter state.
 *
 * Boundary and nearby marker hydration intentionally live in SearchMapIsland
 * so mobile direct-load HDPs do not pay desktop-map API/RSC costs.
 */
export function HdpHydrator({
  maloneLotIdEncoded,
  pdpType,
  unit,
  listing,
  initialMapPosition,
  areaData,
  initialShowBorder,
  hasExplicitBorderParam = false,
}: Props) {
  const queryClient = useQueryClient();
  const locale = useLocale() as SupportedLocale;

  useEffect(() => {
    const pendingListingKey = mapStateKeys.pendingListingNavigation();
    const pendingListingNavigation = queryClient.getQueryData<PendingListingNavigationState>(pendingListingKey);
    // A source listing id may resolve to a canonical HDP URL with a different
    // building id, so the pending marker is provenance-only.
    const isPreparedListingNavigation = !!pendingListingNavigation;

    // 1) Listing detail — matches the key useListingDetail reads from, so the
    //    HDP body renders immediately instead of flashing its skeleton.
    const listingKey = listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, pdpType, unit, locale);
    if (!queryClient.getQueryData(listingKey)) {
      queryClient.setQueryData(listingKey, listing);
    }

    // 1b) Current area — direct HDP URLs have a listing slug in the route, so
    //     the parent layout cannot resolve the area by route param. Seed it from
    //     the listing payload's area data for HeaderNav/filter consumers. Only
    //     when the cache is empty: on SRP → HDP navigation the searched area is
    //     already set, and overwriting it with the listing's neighborhood would
    //     reset the map boundary (HP-8331).
    if (areaData && (isPreparedListingNavigation || !queryClient.getQueryData(currentAreaKeys.current))) {
      queryClient.setQueryData(currentAreaKeys.current, areaData);
      if (areaData.resourceId) {
        queryClient.setQueryData(areaKeys.byResourceId(areaData.resourceId), areaData);
      }
    }

    // 2) Map position — from URL ?lat&lon&z when available, else viewport
    //    computed from listing.geo. Only write if the shell cache is empty so
    //    we don't stomp pan/zoom state preserved across SRP ↔ HDP navigation.
    const positionKey = mapStateKeys.position();
    if (initialMapPosition && (isPreparedListingNavigation || !queryClient.getQueryData(positionKey))) {
      queryClient.setQueryData<MapState>(positionKey, initialMapPosition);
    }

    // 3) Border visibility — URL state is cheap to seed here, but fetching the
    //    boundary waits for SearchMapIsland so mobile HDPs skip that work.
    //    Effect-only storage read, so the stored preference from a previous
    //    session applies to email links that carry no `border` param (HP-8599).
    const borderKey = mapStateKeys.border();
    const resolvedShowBorder = resolveInitialShowBorder(initialShowBorder, hasExplicitBorderParam);
    const cachedBorderState = queryClient.getQueryData<MapBorderState>(borderKey);
    // The resolved value outranks whatever the SRP left in the cache, so a
    // cached border-off state has to be cleared rather than silently inherited —
    // whether it came from an explicit `?border=false` on the previous URL or
    // from SearchMapIsland's boundary-fetch fallbacks, which force the border
    // off without persisting it because a failed fetch is not the user's choice.
    // With no cached state, border-on is already the useMapBorderState default,
    // so skipping the write leaves it alone.
    const cachedBorderContradictsResolved =
      cachedBorderState != null && cachedBorderState.showBorder !== resolvedShowBorder;
    if (isPreparedListingNavigation || !resolvedShowBorder || cachedBorderContradictsResolved) {
      queryClient.setQueryData<MapBorderState>(borderKey, {
        showBorder: resolvedShowBorder,
        isDetecting: false,
        encodedBoundary: null,
        isBoundaryLoading: false,
        boundaryAreaId: null,
      });
    }

    // 4) Active listing — drives SearchMapIsland's syntheticActiveMarker
    //    so the building always pins on the map, even when byCoords returns
    //    zero results or doesn't include this listing.
    const activeListingKey = mapStateKeys.activeListing();
    if ((isPreparedListingNavigation || !queryClient.getQueryData(activeListingKey)) && listing.geo) {
      const geoLat = listing.geo.lat;
      // listing.geo uses `lon`; map state's ActiveListingState uses `lng`.
      const geoLon =
        (listing.geo as { lon?: number; lng?: number }).lon ?? (listing.geo as { lon?: number; lng?: number }).lng;
      if (typeof geoLat === 'number' && typeof geoLon === 'number') {
        queryClient.setQueryData<ActiveListingState>(activeListingKey, {
          listingId: maloneLotIdEncoded,
          lat: geoLat,
          lng: geoLon,
        });
      }
    }

    if (pendingListingNavigation) {
      queryClient.setQueryData<PendingListingNavigationState | null>(pendingListingKey, null);
    }
    // Run once per direct-load HDP page render. The inputs are all server-
    // resolved props and don't change during the page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
